interface AudioManagerOptions {
  loop?: boolean,
  volume?: number,
  playbackRate?: number,
}

const defaultOptions: Required<AudioManagerOptions> = {
  loop: false,
  volume: 1,
  playbackRate: 1,
};

const cloneAudioBuffer = (fromAudioBuffer: AudioBuffer): AudioBuffer => {
  const audioBuffer = new AudioBuffer({
    length: fromAudioBuffer.length,
    numberOfChannels: fromAudioBuffer.numberOfChannels,
    sampleRate: fromAudioBuffer.sampleRate,
  });
  for (let channelI = 0; channelI < audioBuffer.numberOfChannels; ++channelI) {
    const samples = fromAudioBuffer.getChannelData(channelI);
    audioBuffer.copyToChannel(samples, channelI);
  }
  return audioBuffer;
};

const ctx = new AudioContext();

export class AudioManager {

  playing = false;
  pausedAt = 0;
  startedAt = 0;
  options = { ...defaultOptions };
  loadPromise: Promise<AudioBuffer>;
  audioBuffer: AudioBuffer | null = null;
  gainNode: GainNode;
  srcNode: AudioBufferSourceNode | null = null;

  constructor(src: string | AudioManager, options?: AudioManagerOptions) {
    Object.assign(this.options, options);

    this.gainNode = ctx.createGain();
    this.gainNode.gain.value = this.options.volume;
    this.gainNode.connect(ctx.destination);

    this.loadPromise = this.load(src);
  }

  async load(src: string | AudioManager) {
    if (typeof src !== 'string') {
      const originalBuffer = src.audioBuffer;
      this.audioBuffer = cloneAudioBuffer(originalBuffer!);
      return this.audioBuffer;
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }
    const res = await fetch(src);
    const buffer = await res.arrayBuffer();
    this.audioBuffer = await ctx.decodeAudioData(buffer);
    return this.audioBuffer;
  }

  play(when = 0) {
    if (this.playing) return;
    this.playing = true;
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    this.srcNode = ctx.createBufferSource();
    this.srcNode.connect(this.gainNode);
    this.srcNode.buffer = this.audioBuffer;
    this.srcNode.loop = this.options.loop;
    this.srcNode.playbackRate.value = this.options.playbackRate;
    this.srcNode.onended = () => { this.playing = false; };

    this.startedAt = Date.now() - this.pausedAt;
    const { audioBuffer } = this;
    const offset = audioBuffer ? (this.pausedAt / 1000) % audioBuffer.duration : 0;
    this.srcNode.start(when, offset);
  }

  pause() {
    if (!this.playing) return;
    this.playing = false;
    this.pausedAt = Date.now() - this.startedAt;
    if (!this.srcNode) return;
    this.srcNode.stop(0);
  }

  stop() {
    this.playing = false;
    this.srcNode?.disconnect();
    this.srcNode = null;
  }

  clone(options?: AudioManagerOptions) {
    return new AudioManager(this, { ...this.options, ...options });
  }
}
